/**
 * Client Birthday Task Service
 * 
 * Сервис для автоматического создания задач-напоминаний о дне рождения клиентов
 * Соблюдает STYLEGUIDE.md:
 * - camelCase именование
 * - Корректная типизация
 * - Обработка ошибок
 */

import { Task } from '@/types/entities/index';
import { useTasksUnified } from '@/domains/tasks/hooks/useTasksUnified';
import { useAuth } from '@/context/AuthContext';

export interface ClientBirthdayData {
  clientId: string;
  clientName: string;
  clientPhone?: string;
  clientEmail?: string;
  birthDate: Date;
  remindDaysBefore: number;
}

/**
 * Создает задачу-напоминание о дне рождения клиента
 */
export const createBirthdayReminderTask = async (
  clientData: ClientBirthdayData
): Promise<Task | null> => {
  try {
    // Вычисляем дату напоминания
    const currentYear = new Date().getFullYear();
    const birthdayThisYear = new Date(clientData.birthDate);
    birthdayThisYear.setFullYear(currentYear);
    
    // Если день рождения в этом году уже прошел, планируем на следующий год
    const now = new Date();
    if (birthdayThisYear < now) {
      birthdayThisYear.setFullYear(currentYear + 1);
    }
    
    // Дата напоминания - за N дней до дня рождения
    const reminderDate = new Date(birthdayThisYear);
    reminderDate.setDate(reminderDate.getDate() - clientData.remindDaysBefore);
    
    // Создаем задачу-напоминание с контактами клиента
    const contactInfo = [];
    if (clientData.clientPhone) {
      contactInfo.push(`📞 Телефон: ${clientData.clientPhone}`);
    }
    if (clientData.clientEmail) {
      contactInfo.push(`📧 Email: ${clientData.clientEmail}`);
    }
    
    const description = `Напоминание о дне рождения клиента ${clientData.clientName}
День рождения: ${birthdayThisYear.toLocaleDateString('ru-RU')}${contactInfo.length > 0 ? '\n\n' + contactInfo.join('\n') : ''}`;

    const taskData: Omit<Task, 'id' | 'userId' | 'createdAt' | 'updatedAt'> = {
      title: `День рождения: ${clientData.clientName}`,
      description,
      type: 'reminder',
      category: 'client',
      status: 'pending',
      deadline: reminderDate.toISOString(),
      reminderAt: reminderDate.toISOString(),
      relatedClientId: clientData.clientId,
      notification: true,
      completed: false,
    };

    console.log('Creating birthday reminder task:', {
      clientName: clientData.clientName,
      birthDate: birthdayThisYear.toLocaleDateString('ru-RU'),
      reminderDate: reminderDate.toLocaleDateString('ru-RU'),
      daysBefore: clientData.remindDaysBefore
    });

    // Здесь должен быть вызов API для создания задачи
    // Пока используем заглушку
    // const createdTask = await createTask(taskData);
    // return createdTask;
    
    return null; // Временная заглушка
  } catch (error) {
    console.error('Error creating birthday reminder task:', error);
    throw new Error(`Не удалось создать напоминание о дне рождения: ${error}`);
  }
};

/**
 * Обновляет задачу-напоминание о дне рождения клиента
 */
export const updateBirthdayReminderTask = async (
  clientData: ClientBirthdayData,
  existingTaskId?: string
): Promise<Task | null> => {
  try {
    // Если есть существующая задача, обновляем её
    if (existingTaskId) {
      // TODO: Реализовать обновление существующей задачи
      console.log('Updating existing birthday reminder task:', existingTaskId);
    }
    
    // Создаем новую задачу
    return await createBirthdayReminderTask(clientData);
  } catch (error) {
    console.error('Error updating birthday reminder task:', error);
    throw new Error(`Не удалось обновить напоминание о дне рождения: ${error}`);
  }
};

/**
 * Удаляет задачу-напоминание о дне рождения клиента
 */
export const deleteBirthdayReminderTask = async (
  clientId: string
): Promise<void> => {
  try {
    // TODO: Реализовать поиск и удаление задачи по clientId
    console.log('Deleting birthday reminder task for client:', clientId);
  } catch (error) {
    console.error('Error deleting birthday reminder task:', error);
    throw new Error(`Не удалось удалить напоминание о дне рождения: ${error}`);
  }
};

/**
 * Хук для автоматического создания задач-напоминаний о дне рождения
 */
export const useClientBirthdayTasks = () => {
  const { user } = useAuth();
  const { createTask } = useTasksUnified();

  const createBirthdayReminderTaskHook = async (
    clientData: ClientBirthdayData
  ): Promise<Task | null> => {
    if (!user) {
      console.warn('User not authenticated, cannot create birthday reminder task');
      return null;
    }

    try {
      // Вычисляем дату напоминания
      const currentYear = new Date().getFullYear();
      const birthdayThisYear = new Date(clientData.birthDate);
      birthdayThisYear.setFullYear(currentYear);
      
      // Если день рождения в этом году уже прошел, планируем на следующий год
      const now = new Date();
      if (birthdayThisYear < now) {
        birthdayThisYear.setFullYear(currentYear + 1);
      }
      
      // Дата напоминания - за N дней до дня рождения
      const reminderDate = new Date(birthdayThisYear);
      reminderDate.setDate(reminderDate.getDate() - clientData.remindDaysBefore);
      
      // Создаем задачу-напоминание через хук с контактами клиента
      const contactInfo = [];
      if (clientData.clientPhone) {
        contactInfo.push(`📞 Телефон: ${clientData.clientPhone}`);
      }
      if (clientData.clientEmail) {
        contactInfo.push(`📧 Email: ${clientData.clientEmail}`);
      }
      
      const description = `Напоминание о дне рождения клиента ${clientData.clientName}
День рождения: ${birthdayThisYear.toLocaleDateString('ru-RU')}${contactInfo.length > 0 ? '\n\n' + contactInfo.join('\n') : ''}`;

      const taskData: Omit<Task, 'id' | 'userId' | 'createdAt' | 'updatedAt'> = {
        title: `День рождения: ${clientData.clientName}`,
        description,
        type: 'reminder',
        category: 'client',
        status: 'pending',
        deadline: reminderDate.toISOString(),
        reminderAt: reminderDate.toISOString(),
        relatedClientId: clientData.clientId,
        notification: true,
        completed: false,
      };

      console.log('Creating birthday reminder task via hook:', {
        clientName: clientData.clientName,
        birthDate: birthdayThisYear.toLocaleDateString('ru-RU'),
        reminderDate: reminderDate.toLocaleDateString('ru-RU'),
        daysBefore: clientData.remindDaysBefore
      });

      const createdTask = await createTask(taskData);
      return createdTask;
    } catch (error) {
      console.error('Error creating birthday reminder task via hook:', error);
      throw new Error(`Не удалось создать напоминание о дне рождения: ${error}`);
    }
  };

  return {
    createBirthdayReminderTask: createBirthdayReminderTaskHook,
  };
};
